import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { stringFormat, T } from 'src/shared/helpers';
import PatternIcon from 'src/shared/material_icons/device/pattern.svg';
import { CamerasIcon, ObjectsIcon } from 'src/components/widgets/icons/scene';
import { panel, PropertiesPanelLookProvider, PropertiesPanelResourceProperty, PropertiesPanelSegmented } from '../scene-properties-panel';

// I CONTROLLI DELLO SCENE EDITOR NELL'INSPECTOR DEL COMPOSER (utente, 2026-10-15; prototipo V2). Una
// scelta fra due o tre voci è la scelta a segmenti, larga quanto la card; un posto che tiene una cosa —
// la camera, gli oggetti, l'attributo di un Link, l'opzione dello store — è lo slot: il glifo in un
// riquadro, il nome, che cos'è, e la riga si apre sull'elenco. Prima erano radio in fila e tendine di
// react-select: un'altra grammatica a un pannello di distanza.

export type ComposerSlotOption = {
    value: string;
    label: string;
    caption?: string;
    /** La faccia della voce, quando ne ha una sua: l'immagine di un'opzione. Senza, il glifo dello slot. */
    preview?: ReactNode;
};

// IL GLIFO STA IN UN RIQUADRO, come nella marca della testata: è il segno della specie, non una foto.
export const ComposerSlotGlyph = styled.span`
    display: inline-flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 6px;
    background: ${panel.ground};
    box-shadow: inset 0 0 0 1px ${panel.line};
    color: ${panel.inkMuted};

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

// UN ERRORE SI VEDE SUL POSTO: il bordo dello slot — o di quello vuoto, in un elenco — si fa rosso.
const SlotFrame = styled.div<{ $error?: boolean }>`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 6px;

    ${(props) =>
        props.$error &&
        `&[data-slot-error] [id],
        [data-slot-error] [id] {
            border-color: ${props.theme.app.component.button.destructive.default.backgroundColor};
        }`}
`;

/** Una scelta fra poche voci, a tutta riga. I valori arrivano come sono — numeri compresi — e tornano uguali. */
export function ComposerChoice<V extends string | number>({
    options,
    value,
    onChange,
    ariaLabel,
    disabled
}: {
    options: { value: V; label: string }[];
    value: V;
    onChange: (value: V) => void;
    ariaLabel: string;
    disabled?: boolean;
}) {
    return (
        <div data-composer-wide>
            <PropertiesPanelSegmented
                fill
                disabled={disabled}
                ariaLabel={ariaLabel}
                options={options.map((option) => ({ value: String(option.value), label: option.label }))}
                value={String(value)}
                onChange={(chosen) => {
                    const picked = options.find((option) => String(option.value) === chosen);
                    if (picked) onChange(picked.value);
                }}
            />
        </div>
    );
}

type SlotCommon = {
    id: string;
    options: ComposerSlotOption[];
    /** Il segno della specie, per le voci senza una faccia loro. */
    glyph: ReactNode;
    /** Sotto il nome, quando la voce non dice di più: «Camera», «Object». */
    caption?: string;
    emptyLabel: string;
    emptyCaption?: string;
    searchPlaceholder?: string;
    /** Che cosa dice l'elenco quando non c'è niente da scegliere. */
    noOptionsLabel?: string;
    isError?: boolean;
    disabled?: boolean;
};

const glyphed = (content: ReactNode) => <ComposerSlotGlyph>{content}</ComposerSlotGlyph>;
const pickerOptions = (options: ComposerSlotOption[], glyph: ReactNode) =>
    options.map((option) => ({ value: option.value, label: option.label, caption: option.caption, mark: glyphed(option.preview ?? glyph) }));

/** UN POSTO, UNA COSA: si sceglie dall'elenco e, se si può fare a meno, si svuota col cestino. */
export const ComposerSlot: FC<
    SlotCommon & {
        value?: string;
        onChange: (value: string) => void;
        /** Si può lasciare vuoto: c'è il cestino. */
        clearable?: boolean;
        /** Il nome di una voce che l'elenco non ha più. */
        missingLabel?: string;
    }
> = ({
    id,
    value,
    options,
    glyph,
    caption,
    emptyLabel,
    emptyCaption,
    searchPlaceholder,
    noOptionsLabel,
    isError,
    disabled,
    onChange,
    clearable,
    missingLabel
}) => {
    const current = value ? (options.find((option) => option.value === value) ?? { value, label: missingLabel ?? value }) : undefined;
    return (
        // LO SLOT HA SEMPRE IL VESTITO DELLA SCENA: fuori da una sezione — una card d'azione, un'espressione dei
        // Links — prendeva quello vecchio, con il filo tratteggiato sotto e il proprio respiro.
        <PropertiesPanelLookProvider value='scene'>
            <SlotFrame $error={isError} data-slot-error data-composer-wide>
                <PropertiesPanelResourceProperty
                    id={id}
                    preview={current ? glyphed(current.preview ?? glyph) : undefined}
                    name={current?.label}
                    caption={current?.caption ?? caption}
                    emptyLabel={emptyLabel}
                    emptyCaption={emptyCaption}
                    options={pickerOptions(options, glyph)}
                    value={current?.value}
                    searchPlaceholder={searchPlaceholder}
                    noOptionsLabel={noOptionsLabel}
                    disabled={disabled}
                    onSelect={(chosen) => onChange(String(chosen))}
                    onClear={current && clearable ? () => onChange('') : undefined}
                />
            </SlotFrame>
        </PropertiesPanelLookProvider>
    );
};

/**
 * TANTE COSE: UNO SLOT PER COSA, E UNO VUOTO PER AGGIUNGERNE (prototipo, «Choose an object»). Ognuno
 * si sostituisce o si toglie da sé; l'elenco non ripropone quelle già prese. Con un errore, si accende
 * il posto vuoto: è lì che manca qualcosa.
 */
export const ComposerSlotList: FC<
    SlotCommon & {
        value: string[];
        onChange: (values: string[]) => void;
        missingLabel?: string;
        /** Una voce da aggiungere che nasce dal testo cercato: un modello di nome. Restituisce il valore da scrivere. */
        fromQuery?: (query: string) => (Omit<ComposerSlotOption, 'preview'> & { mark?: ReactNode }) | null;
    }
> = ({
    id,
    value,
    options,
    glyph,
    caption,
    emptyLabel,
    emptyCaption,
    searchPlaceholder,
    noOptionsLabel,
    isError,
    disabled,
    onChange,
    missingLabel,
    fromQuery
}) => {
    const optionOf = (chosen: string) => options.find((option) => option.value === chosen) ?? { value: chosen, label: missingLabel ?? chosen };
    const free = (keep?: string) => options.filter((option) => option.value === keep || !value.includes(option.value));
    return (
        <PropertiesPanelLookProvider value='scene'>
            <SlotFrame $error={isError} data-composer-wide>
                {value.map((chosen, index) => {
                    const current = optionOf(chosen);
                    return (
                        <PropertiesPanelResourceProperty
                            key={chosen}
                            id={`${id}-${index}`}
                            preview={glyphed(current.preview ?? glyph)}
                            name={current.label}
                            caption={current.caption ?? caption}
                            options={pickerOptions(free(chosen), glyph)}
                            value={chosen}
                            searchPlaceholder={searchPlaceholder}
                            disabled={disabled}
                            onSelect={(picked) => onChange(value.map((candidate) => (candidate === chosen ? String(picked) : candidate)))}
                            onClear={() => onChange(value.filter((candidate) => candidate !== chosen))}
                        />
                    );
                })}
                {(free().length > 0 || (options.length === 0 && noOptionsLabel) || fromQuery) && (
                    <div data-slot-error>
                        <PropertiesPanelResourceProperty
                            id={`${id}-new`}
                            emptyLabel={emptyLabel}
                            emptyCaption={emptyCaption}
                            options={pickerOptions(free(), glyph)}
                            searchPlaceholder={searchPlaceholder}
                            noOptionsLabel={noOptionsLabel}
                            fromQuery={
                                fromQuery &&
                                ((query) => {
                                    const entry = fromQuery(query);
                                    return (
                                        entry && {
                                            label: entry.label,
                                            caption: entry.caption,
                                            mark: entry.mark,
                                            onClick: () => onChange([...value, entry.value])
                                        }
                                    );
                                })
                            }
                            disabled={disabled}
                            onSelect={(picked) => onChange([...value, String(picked)])}
                        />
                    </div>
                )}
            </SlotFrame>
        </PropertiesPanelLookProvider>
    );
};

// IL GUID TUTTO ZERI È «NESSUNA CAMERA»: così la salva il Backoffice, e lo slot lo mostrava come un nome.
const NO_CAMERA = '00000000-0000-0000-0000-000000000000';

/** La posizione di camera. */
export const ComposerCameraSlot: FC<{
    id: string;
    value?: string;
    options: ComposerSlotOption[];
    onChange: (value: string) => void;
    isError?: boolean;
    /** Si può lasciare vuota: c'è il cestino. Un'azione «Move the camera» invece una camera la vuole. */
    clearable?: boolean;
}> = ({ clearable = true, ...props }) => (
    <ComposerSlot
        {...props}
        value={props.value && props.value !== NO_CAMERA ? props.value : undefined}
        noOptionsLabel={T._('The scene has no saved cameras yet.', 'Admin')}
        glyph={<CamerasIcon />}
        caption={T._('Camera', 'Admin')}
        emptyLabel={T._('Choose a camera position', 'Admin')}
        searchPlaceholder={T._('Search cameras…', 'Admin')}
        clearable={clearable}
    />
);

// I MODELLI DI NOME (utente, 2026-10-15). Nei bersagli di un'azione il motore legge ogni voce come un modello:
// «*» vale qualunque testo, e il confronto col nome della mesh ignora maiuscole e minuscole — «*» prende tutte
// le mesh, «Seat*» quelle che portano «Seat» nel nome. Il Backoffice li accettava già: qui si scrivono nella
// ricerca dello slot e diventano uno slot loro, che dice quante mesh prendono adesso.
const patternOf = (text: string) => new RegExp(text.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*'), 'i');
const isPattern = (text: string) => text.includes('*');

/** Gli oggetti della scena. Con `allowPatterns`, anche «*» e i modelli di nome. */
export const ComposerObjectSlots: FC<{
    id: string;
    value: string[];
    options: ComposerSlotOption[];
    onChange: (ids: string[]) => void;
    isError?: boolean;
    allowPatterns?: boolean;
}> = ({ allowPatterns, options, value, onChange, ...props }) => {
    const patternGlyph = <PatternIcon />;
    const matches = (pattern: string) => {
        const expression = patternOf(pattern);
        const count = options.filter((option) => expression.test(option.label)).length;
        return count === 1 ? T._('1 object now', 'Admin') : stringFormat(T._('{0} objects now', 'Admin'), count.toString());
    };
    const describe = (pattern: string): ComposerSlotOption =>
        pattern === '*'
            ? { value: pattern, label: T._('All objects', 'Admin'), caption: `* · ${matches(pattern)}`, preview: patternGlyph }
            : { value: pattern, label: pattern, caption: stringFormat(T._('Name pattern · {0}', 'Admin'), matches(pattern)), preview: patternGlyph };
    const shown = allowPatterns ? [...options, ...value.filter(isPattern).map(describe)] : options;
    return (
        <ComposerSlotList
            {...props}
            value={value}
            onChange={onChange}
            options={shown}
            glyph={<ObjectsIcon />}
            caption={T._('Object', 'Admin')}
            emptyLabel={T._('Choose an object', 'Admin')}
            noOptionsLabel={T._('The scene has no objects yet.', 'Admin')}
            searchPlaceholder={allowPatterns ? T._('Name or pattern: Seat*', 'Admin') : T._('Search objects…', 'Admin')}
            fromQuery={
                allowPatterns
                    ? (query) => {
                          // Senza testo, in testa c'è «tutte»; con un «*» nel testo, il modello scritto.
                          if (!query) return value.includes('*') ? null : { ...describe('*'), mark: patternGlyph };
                          if (!isPattern(query) || value.includes(query)) return null;
                          return { ...describe(query), label: stringFormat(T._('Use the pattern “{0}”', 'Admin'), query), mark: patternGlyph };
                      }
                    : undefined
            }
        />
    );
};
